/**
 * Whether a series can honestly be reformatted.
 *
 * **A MULTIPLANAR REFORMAT FAILS SILENTLY, WHICH IS WHY THIS EXISTS.**
 * Given slices with gaps, duplicated positions or a mix of planes, it
 * still produces a smooth, plausible coronal and sagittal — just of an
 * anatomy the patient does not have. Phase 0 hit exactly that on a real
 * export: a 12.53mm step through a 0.7mm acquisition, reconstructed
 * without a word.
 *
 * So the viewer decides BEFORE it offers the button, from geometry
 * stored at ingest, and it says why when the answer is no. A reformat
 * that refuses is a small annoyance; one that invents is a reading
 * error nobody can see.
 *
 * The checks are deliberately conservative. Everything rejected here is
 * still perfectly readable as a stack, which is what a radiologist would
 * be doing anyway.
 */

export interface SliceGeometry {
  slice_position?: number | null;
  orientation_key?: string | null;
}

export type MprVerdict =
  | { ok: true; spacing: number; count: number }
  | { ok: false; reason: string };

/** Fewer than this and a reformat has nothing to reconstruct — the
 *  out-of-plane views would be a handful of pixels tall. */
const MIN_SLICES = 8;

/**
 * How much the spacing may vary, as a fraction of the median gap.
 *
 * 1% is tighter than it may look and is the right order: a CT
 * reconstructed at 0.7mm carries positions to three decimals, so real
 * slices of one acquisition agree to within microns. Anything past this
 * is a genuine step, not rounding.
 */
const SPACING_TOLERANCE = 0.01;

export function mprVerdict(frames: SliceGeometry[]): MprVerdict {
  if (frames.length < MIN_SLICES) {
    return { ok: false, reason: `Needs at least ${MIN_SLICES} slices` };
  }

  const planes = new Set(frames.map((f) => f.orientation_key ?? ""));
  if (planes.has("")) {
    // An ultrasound still, a plain film, or anything ingested before the
    // geometry was stored. The ordinary case, not a fault.
    return { ok: false, reason: "This series has no slice geometry" };
  }
  if (planes.size > 1) {
    // THE ONE THAT LOOKS FINE. Positions can be tidy and evenly spaced
    // while the slices lie in different planes — a localiser swept into
    // the series, or a study reconstructed twice.
    return { ok: false, reason: "This series mixes more than one plane" };
  }

  const positions = frames
    .map((f) => f.slice_position)
    .filter((p): p is number => typeof p === "number")
    .sort((a, b) => a - b);
  if (positions.length !== frames.length) {
    return { ok: false, reason: "This series has no slice geometry" };
  }

  const gaps: number[] = [];
  for (let i = 1; i < positions.length; i += 1) {
    gaps.push(positions[i]! - positions[i - 1]!);
  }

  const sorted = [...gaps].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)]!;
  if (median <= 0) {
    // Two slices at the same position: a duplicate, or an interleaved
    // acquisition sorted by the wrong key.
    return { ok: false, reason: "Slices repeat the same position" };
  }

  const worst = Math.max(...gaps.map((g) => Math.abs(g - median)));
  if (worst / median > SPACING_TOLERANCE) {
    // Named in millimetres rather than as "irregular", because the
    // number is what tells a radiologist whether it is a missing slice
    // or a differently reconstructed study.
    return {
      ok: false,
      reason: `Uneven slice spacing (${median.toFixed(2)}mm, worst gap off by ${worst.toFixed(2)}mm)`,
    };
  }

  return { ok: true, spacing: median, count: positions.length };
}

/**
 * The stack in ANATOMICAL order.
 *
 * Not InstanceNumber, which is what the listing endpoint sorts by and
 * is only usually the same thing: a study reconstructed in two passes,
 * or one exported by a system that renumbered, gives a stack that looks
 * ordered and steps backwards through the body somewhere in the middle.
 * Position is the physical truth and is what a volume must be built
 * from.
 *
 * Returns the input untouched when any slice lacks a position, so the
 * ordinary stack path is never re-ordered on a guess.
 */
export function inSliceOrder<T extends SliceGeometry>(frames: T[]): T[] {
  if (frames.some((f) => typeof f.slice_position !== "number")) return frames;
  return [...frames].sort(
    (a, b) => (a.slice_position as number) - (b.slice_position as number),
  );
}
